// ----------------------------------------------------------------------

import { isValidDateStrict } from 'src/utils/format-time';

/**
 * Fallback span for the full-width rows (empty state, loading state). Enough for
 * the fixed-column tables these were written for, where a span wider than the
 * table is clamped to it. Grids whose column count is user-configurable must
 * pass their real count, or the row stops short of the trailing columns.
 */
export const DEFAULT_TABLE_COL_SPAN = 12;

export function rowInPage<T>(data: T[], page: number, rowsPerPage: number) {
  return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
}

// ----------------------------------------------------------------------

export function emptyRows(page: number, rowsPerPage: number, arrayLength: number) {
  return page ? Math.max(0, (1 + page) * rowsPerPage - arrayLength) : 0;
}

/**
 * @description This function should be used in <TableEmptyRows /> when doing server-side pagination;
 * keep the above function because some pages are still using it correctly
 */
export function emptyRowsWithServerPagination(rowsPerPage: number, arrayLength: number) {
  return rowsPerPage - arrayLength;
}

// ----------------------------------------------------------------------

/**
 * @example
 * const data = {
 *   calories: 360,
 *   align: 'center',
 *   more: {
 *     protein: 42,
 *   },
 * };
 *
 * const ex1 = getNestedProperty(data, 'calories');
 * console.log('ex1', ex1); // output: 360
 *
 * const ex2 = getNestedProperty(data, 'align');
 * console.log('ex2', ex2); // output: center
 *
 * const ex3 = getNestedProperty(data, 'more.protein');
 * console.log('ex3', ex3); // output: 42
 */
function getNestedProperty<T>(obj: T, key: string): any {
  // Subtotal is a union of two fields
  if (key === 'subTotal') return Number((obj as any).quantity) * Number((obj as any).unitPrice);
  return key.split('.').reduce((acc: any, part: string) => acc && acc[part], obj);
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T, orderType?: string) {
  let aValue = getNestedProperty(a, orderBy as string);
  let bValue = getNestedProperty(b, orderBy as string);

  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;

  aValue = convertToComparable(aValue, orderType);
  bValue = convertToComparable(bValue, orderType);

  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

// ----------------------------------------------------------------------

export function getComparator<Key extends keyof any>(
  order: 'asc' | 'desc',
  orderBy: Key,
  orderType?: string
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy, orderType)
    : (a, b) => -descendingComparator(a, b, orderBy, orderType);
}

const convertToComparable = (
  value: string | number | boolean | Date,
  orderType?: string
): number | string => {
  if (orderType == 'string') {
    return value.toString().toLowerCase();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return +value;
  }

  if (typeof value === 'string') {
    if (isValidDateStrict(value, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss.SSS[Z]'])) {
      return new Date(value).getTime();
    }

    const num = parseFloat(value);

    if (isNaN(num)) {
      return value.toLowerCase();
    } else {
      return num;
    }
  }

  return value;
};
